import React, { useState, useEffect, useCallback } from 'react';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/solid';
import { walletCodeApi, walletCodeCategoryApi } from '../utils/api';
import type { WalletCodeCategoryDto, WalletCodeDto } from '../types/wallet';

interface Filters {
  categoryId: string;
  code: string;
  isUsed: string;
  createdDateStart: string;
  createdDateEnd: string;
}

const WalletCodes: React.FC = () => {
  const [categories, setCategories] = useState<WalletCodeCategoryDto[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [showCategoriesListModal, setShowCategoriesListModal] = useState(false);
  const [showCategoryFormModal, setShowCategoryFormModal] = useState(false);
  const [categoryForm, setCategoryForm] = useState({
    id: '',
    name: '',
    description: '',
    price: 0,
    isActive: true,
  });
  const [editingCategory, setEditingCategory] = useState(false);

  const [codes, setCodes] = useState<WalletCodeDto[]>([]);
  const [loadingCodes, setLoadingCodes] = useState(false);
  const [showCodeModal, setShowCodeModal] = useState(false);
  const [codeForm, setCodeForm] = useState({
    categoryId: '',
    name: '',
    count: 1,
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [itemsPerPage] = useState(10);

  const [filters, setFilters] = useState<Filters>({
    categoryId: '',
    code: '',
    isUsed: '',
    createdDateStart: '',
    createdDateEnd: '',
  });
  const [appliedFilters, setAppliedFilters] = useState<Filters>({
    categoryId: '',
    code: '',
    isUsed: '',
    createdDateStart: '',
    createdDateEnd: '',
  });

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ type: 'category' | 'code'; id: string } | null>(null);

  const fetchCategories = useCallback(async () => {
    setLoadingCategories(true);
    try {
      const data = await walletCodeCategoryApi.getAll();
      if (data.success) {
        setCategories(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoadingCategories(false);
    }
  }, []);

  const fetchCodes = useCallback(async () => {
    setLoadingCodes(true);
    try {
      const data = await walletCodeApi.getAll({
        pageNumber: currentPage,
        pageSize: itemsPerPage,
        categoryId: appliedFilters.categoryId || undefined,
        searchTerm: appliedFilters.code || undefined,
        isUsed: appliedFilters.isUsed === '' ? undefined : appliedFilters.isUsed === 'true',
        createdFrom: appliedFilters.createdDateStart || undefined,
        createdTo: appliedFilters.createdDateEnd || undefined,
      });

      if (data.success) {
        setCodes(data.data?.data || []);
        setTotalItems(data.data?.totalCount || 0);
        setTotalPages(data.data?.totalPages || 0);
      }
    } catch (error) {
      console.error('Error fetching codes:', error);
    } finally {
      setLoadingCodes(false);
    }
  }, [currentPage, itemsPerPage, appliedFilters]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    fetchCodes();
  }, [fetchCodes]);

  const handleOpenCategoryForm = (category?: WalletCodeCategoryDto) => {
    if (category) {
      setCategoryForm({
        id: category.id,
        name: category.name,
        description: category.description || '',
        price: category.price,
        isActive: category.isActive,
      });
      setEditingCategory(true);
    } else {
      setCategoryForm({
        id: '',
        name: '',
        description: '',
        price: 0,
        isActive: true,
      });
      setEditingCategory(false);
    }
    setShowCategoryFormModal(true);
  };

  const handleSaveCategory = async () => {
    try {
      const payload = {
        name: categoryForm.name,
        description: categoryForm.description,
        price: categoryForm.price,
        isActive: categoryForm.isActive,
      };

      const data = editingCategory
        ? await walletCodeCategoryApi.update(categoryForm.id, { ...payload, id: categoryForm.id })
        : await walletCodeCategoryApi.create(payload);

      if (data.success) {
        alert(editingCategory ? 'تم تحديث الفئة بنجاح' : 'تم إنشاء الفئة بنجاح');
        setShowCategoryFormModal(false);
        fetchCategories();
        fetchCodes();
      } else {
        alert(data.message || 'حدث خطأ');
      }
    } catch (error) {
      console.error('Error saving category:', error);
      alert('حدث خطأ أثناء حفظ الفئة');
    }
  };

  const handleCreateCodes = async () => {
    if (!codeForm.categoryId || !codeForm.name.trim() || codeForm.count < 1) {
      alert('الرجاء اختيار الفئة وإدخال اسم الدفعة وعدد صحيح من الأكواد');
      return;
    }

    try {
      const data = await walletCodeApi.create({
        categoryId: codeForm.categoryId,
        name: codeForm.name.trim(),
        count: codeForm.count,
      });

      if (data.success) {
        alert(`تم إنشاء ${codeForm.count} كود بنجاح`);
        setShowCodeModal(false);
        fetchCategories();
        fetchCodes();
      } else {
        alert(data.message || 'حدث خطأ');
      }
    } catch (error) {
      console.error('Error creating codes:', error);
      alert('حدث خطأ أثناء إنشاء الأكواد');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      const data =
        deleteTarget.type === 'category'
          ? await walletCodeCategoryApi.delete(deleteTarget.id)
          : await walletCodeApi.delete(deleteTarget.id);

      if (data.success) {
        alert('تم الحذف بنجاح');
        setShowDeleteConfirm(false);
        setDeleteTarget(null);
        if (deleteTarget.type === 'category') {
          fetchCategories();
        }
        fetchCodes();
      } else {
        alert(data.message || 'حدث خطأ');
      }
    } catch (error) {
      console.error('Error deleting:', error);
      alert('حدث خطأ أثناء الحذف');
    }
  };

  const handleSearch = () => {
    setAppliedFilters(filters);
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    const resetFilters: Filters = {
      categoryId: '',
      code: '',
      isUsed: '',
      createdDateStart: '',
      createdDateEnd: '',
    };
    setFilters(resetFilters);
    setAppliedFilters(resetFilters);
    setCurrentPage(1);
  };

  return (
    <div className="p-6" dir="rtl">
      <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
        <h1 className="text-2xl font-bold">أكواد المحفظة</h1>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setShowCategoriesListModal(true)}
            className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2"
          >
            <PlusIcon className="w-5 h-5" />
            الفئات
          </button>
          <button
            onClick={() => {
              setCodeForm({ categoryId: '', name: '', count: 1 });
              setShowCodeModal(true);
            }}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
          >
            <PlusIcon className="w-5 h-5" />
            إنشاء أكواد
          </button>
        </div>
      </div>

      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <h2 className="text-lg font-semibold mb-4">الفلاتر</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">الفئة</label>
            <select
              value={filters.categoryId}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                setFilters({ ...filters, categoryId: e.target.value })
              }
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
            >
              <option value="">كل الفئات</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">الكود</label>
            <input
              type="text"
              value={filters.code}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setFilters({ ...filters, code: e.target.value })
              }
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
              placeholder="البحث بالكود أو اسم الدفعة"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">الحالة</label>
            <select
              value={filters.isUsed}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                setFilters({ ...filters, isUsed: e.target.value })
              }
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
            >
              <option value="">الكل</option>
              <option value="false">غير مستخدم</option>
              <option value="true">مستخدم</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">من تاريخ</label>
            <input
              type="date"
              value={filters.createdDateStart}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setFilters({ ...filters, createdDateStart: e.target.value })
              }
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">إلى تاريخ</label>
            <input
              type="date"
              value={filters.createdDateEnd}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setFilters({ ...filters, createdDateEnd: e.target.value })
              }
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>
        </div>
        <div className="mt-4 flex justify-start space-x-reverse space-x-4">
          <button
            onClick={handleSearch}
            className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors"
          >
            بحث
          </button>
          <button
            onClick={handleResetFilters}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          >
            إعادة تعيين
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-4 border-b">
          <span className="font-medium">المجموع: {totalItems}</span>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">الكود</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">اسم الدفعة</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">الفئة</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">السعر</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">العملات</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">الحالة</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">الطالب</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">تاريخ الاستخدام</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">تاريخ الإنشاء</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">الإجراءات</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loadingCodes ? (
                <tr>
                  <td colSpan={10} className="px-6 py-12 text-center">
                    <div className="flex justify-center">
                      <svg
                        className="animate-spin h-8 w-8 text-primary"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                    </div>
                  </td>
                </tr>
              ) : codes.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-6 py-4 text-center text-gray-500">
                    لا توجد أكواد
                  </td>
                </tr>
              ) : (
                codes.map((code) => (
                  <tr key={code.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap font-medium">{code.code}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{code.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{code.categoryName}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{code.price} جنيه</td>
                    <td className="px-6 py-4 whitespace-nowrap">{code.value}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${
                          code.isUsed ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}
                      >
                        {code.isUsed ? 'مستخدم' : 'غير مستخدم'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">{code.usedByStudentName || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {code.usedAt ? new Date(code.usedAt).toLocaleDateString('ar-EG') : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {new Date(code.createdAt).toLocaleDateString('ar-EG')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {!code.isUsed && (
                        <button
                          onClick={() => {
                            setDeleteTarget({ type: 'code', id: code.id });
                            setShowDeleteConfirm(true);
                          }}
                          className="text-red-600 hover:text-red-800"
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalItems > 0 && !loadingCodes && (
          <div className="px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="text-sm text-gray-700">
              عرض {(currentPage - 1) * itemsPerPage + 1} إلى{' '}
              {Math.min(currentPage * itemsPerPage, totalItems)} من {totalItems}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className={`px-3 py-1 border rounded ${
                  currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                السابق
              </button>
              <span className="px-3 py-1">
                صفحة {currentPage} من {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className={`px-3 py-1 border rounded ${
                  currentPage === totalPages ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                التالي
              </button>
            </div>
          </div>
        )}
      </div>

      {showCategoriesListModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-4xl max-h-[90vh] flex flex-col">
            <div className="p-4 border-b flex justify-between items-center">
              <h2 className="text-xl font-semibold">فئات الأكواد</h2>
              <button
                onClick={() => handleOpenCategoryForm()}
                className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2"
              >
                <PlusIcon className="w-5 h-5" />
                إضافة فئة
              </button>
            </div>

            <div className="overflow-auto flex-1">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">الاسم</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">الوصف</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">السعر</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">الأكواد</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">الحالة</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">تاريخ الإنشاء</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">الإجراءات</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loadingCategories ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-12 text-center">
                        <div className="flex justify-center">
                          <svg
                            className="animate-spin h-8 w-8 text-primary"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                          </svg>
                        </div>
                      </td>
                    </tr>
                  ) : categories.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                        لا توجد فئات
                      </td>
                    </tr>
                  ) : (
                    categories.map((category) => (
                      <tr key={category.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">{category.name}</td>
                        <td className="px-6 py-4">{category.description}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{category.price} جنيه</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {category.totalCodes ?? 0} إجمالي / {category.usedCodes ?? 0} مستخدم /{' '}
                          {category.availableCodes ?? 0} متاح
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 text-xs rounded-full ${
                              category.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {category.isActive ? 'نشط' : 'غير نشط'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {new Date(category.createdAt).toLocaleDateString('ar-EG')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleOpenCategoryForm(category)}
                              className="text-blue-600 hover:text-blue-800"
                            >
                              <PencilIcon className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => {
                                setDeleteTarget({ type: 'category', id: category.id });
                                setShowDeleteConfirm(true);
                              }}
                              className="text-red-600 hover:text-red-800"
                            >
                              <TrashIcon className="w-5 h-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="p-4 border-t">
              <button
                onClick={() => setShowCategoriesListModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                إغلاق
              </button>
            </div>
          </div>
        </div>
      )}

      {showCategoryFormModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4">
              {editingCategory ? 'تعديل الفئة' : 'إضافة فئة جديدة'}
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">الاسم</label>
                <input
                  type="text"
                  value={categoryForm.name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setCategoryForm({ ...categoryForm, name: e.target.value })
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  placeholder="اسم الفئة"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">الوصف</label>
                <textarea
                  value={categoryForm.description}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    setCategoryForm({ ...categoryForm, description: e.target.value })
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  placeholder="وصف الفئة"
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">السعر (جنيه)</label>
                <input
                  type="number"
                  value={categoryForm.price}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setCategoryForm({ ...categoryForm, price: Number(e.target.value) })
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  min="0"
                  step="0.01"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={categoryForm.isActive}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setCategoryForm({ ...categoryForm, isActive: e.target.checked })
                  }
                  className="w-4 h-4 text-primary focus:ring-primary border-gray-300 rounded"
                />
                <label htmlFor="isActive" className="mr-2 text-sm text-gray-700">
                  نشط
                </label>
              </div>
            </div>

            <div className="mt-6 flex justify-start gap-2">
              <button
                onClick={() => setShowCategoryFormModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                إلغاء
              </button>
              <button
                onClick={handleSaveCategory}
                className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors"
              >
                حفظ
              </button>
            </div>
          </div>
        </div>
      )}

      {showCodeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4">إنشاء أكواد جديدة</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">الفئة</label>
                <select
                  value={codeForm.categoryId}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    setCodeForm({ ...codeForm, categoryId: e.target.value })
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                >
                  <option value="">اختر الفئة</option>
                  {categories
                    .filter((c) => c.isActive)
                    .map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name} - {cat.price} جنيه
                      </option>
                    ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">اسم الدفعة</label>
                <input
                  type="text"
                  value={codeForm.name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setCodeForm({ ...codeForm, name: e.target.value })
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  placeholder="مثال: دفعة يناير 2026"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">عدد الأكواد</label>
                <input
                  type="number"
                  value={codeForm.count}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setCodeForm({ ...codeForm, count: Number(e.target.value) })
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  min="1"
                  max="10000"
                />
              </div>
            </div>

            <div className="mt-6 flex justify-start gap-2">
              <button
                onClick={() => setShowCodeModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                إلغاء
              </button>
              <button
                onClick={handleCreateCodes}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
              >
                إنشاء
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4">تأكيد الحذف</h2>
            <p className="text-gray-600 mb-6">
              هل أنت متأكد من حذف هذا {deleteTarget?.type === 'category' ? 'الفئة' : 'الكود'}؟
            </p>
            <div className="flex justify-start gap-2">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteTarget(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                إلغاء
              </button>
              <button
                onClick={handleDelete}
                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
              >
                حذف
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WalletCodes;
